<script>
import { mapActions, mapGetters } from 'vuex';
import RbxConfirmDialog from '@/shared/reybex/RbxConfirmDialog';
import downloader from '@/mixins/downloader';
import mainViewTabMixin from '../../mixins/mainViewTab';
import snackbarMixin from '../../mixins/snackbar';
import MasterDataForm from './components/MasterDataForm';
import ConfidentialityForm from './components/ConfidentialityForm';
import IntegrityForm from './components/IntegrityForm';
import EvaluationForm from './components/EvaluationForm';
import TaskList from '../taskList';
import Notes from '../notes';
import History from '../history';
import Documents from './components/Documents';

export default {
  name: 'TomDetails',
  components: {
    MasterDataForm,
    ConfidentialityForm,
    IntegrityForm,
    EvaluationForm,
    TaskList,
    Notes,
    History,
    Documents,
    RbxConfirmDialog,
  },
  mixins: [downloader, mainViewTabMixin, snackbarMixin],
  data() {
    return {
      query: {},
      params: {},
      tom: {},
      isLoading: false,
      isSaving: false,
      hasError: false,
      activeTab: 0,
      isDownloading: false,
      isNewVersionConfirmDialogDisplayed: false,
    };
  },
  computed: {
    ...mapGetters('app', ['auth']),
  },
  created() {
    const { query, params } = this.urlParser('/tom/show/:id?', this.path);

    this.query = query;
    this.params = params;
    this.fetchData();
  },
  methods: {
    ...mapActions('tomDetails', ['fetchTom', 'saveTom', 'copyTom']),
    ...mapActions('tomList', ['downloadReport']),
    fetchData() {
      this.isLoading = true;
      this.hasError = false;

      return this.fetchTom(this.params.id)
        .then(({ data }) => {
          this.tom = data;
          this.updateTabTitle({ path: this.path, title: this.tom.name });
        })
        .catch(() => {
          this.hasError = true;
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    save() {
      this.isSaving = true;

      return this.saveTom(this.tom)
        .then(() => {
          this.showSuccessMessage('Die Technisch-Organisatorische Einheit wurde gespeichert.');
        })
        .catch(() => {
          this.showErrorMessage('Die Aktualisierung ist fehlgeschlagen.');
        })
        .finally(() => {
          this.isSaving = false;
        });
    },
    _downloadReport() {
      this.isDownloading = true;
      const self = this;
      this.downloadReport(this.tom.id)
        .then((response) => {
          console.log('response', response);
          this.getDownloadContent(response, 'tom_report.pdf');
        }).catch((error) => {
          console.log(`Fehler: ${error.response}`);
          this.showErrorMessage('Beim Erstellen des Berichts ist ein Fehler aufgetreten.');
        }).finally(() => {
          self.isDownloading = false;
        });
    },
    onNewVersionConfirm() {
      this.isNewVersionConfirmDialogDisplayed = false;
      this.isSaving = true;

      this.copyTom(this.tom)
        .then((response) => {
          this.showSuccessMessage('Die Technisch-Organisatorische Einheit wurde als neue Version angelegt.');
          this.params.id = response.data.id;
          this.fetchData();
        })
        .catch(() => {
          this.showErrorMessage('Das Speichern ist fehlgeschlagen.');
        })
        .finally(() => {
          this.isSaving = false;
        });
    },
    onNewVersionRefuse() {
      this.isNewVersionConfirmDialogDisplayed = false;
      this.save();
    },
  },
};
</script>

<template>
  <div class="tom-detail">
    <v-progress-linear v-if="isLoading" :indeterminate="true"/>

    <span
      v-else-if="hasError"
      class="js-error ma-3"
    >An error occurred while fetching tom information</span>

    <template v-else>
      <v-toolbar dark dense height="40" color="blue-grey lighten-2">
        <v-tooltip bottom>
          <v-btn @click="fetchData" icon slot="activator">
            <v-icon>refresh</v-icon>
          </v-btn>
          <span>Aktualisieren</span>
        </v-tooltip>

        <v-tooltip bottom>
          <v-btn
            slot="activator"
            :loading="isSaving"
            icon
            @click="isNewVersionConfirmDialogDisplayed = true"
          >
            <v-icon>save</v-icon>
          </v-btn>
          <span>Speichern</span>
        </v-tooltip>

        <v-tooltip bottom>
          <v-btn
            v-if="auth.hasDownloadRights"
            slot="activator"
            :disabled="isDownloading"
            flat icon
            @click.native="_downloadReport"
          >
            <v-icon small>cloud_download</v-icon>
          </v-btn>
          <span>Bericht herunterladen</span>
        </v-tooltip>

      </v-toolbar>

      <v-tabs v-model="activeTab"
              height="35"
              slider-color="black"
              color="amber">

        <v-tab ripple>Stammdaten</v-tab>
        <v-tab ripple>Vertraulichkeit</v-tab>
        <v-tab ripple>Integrität/Verfügbarkeit</v-tab>
        <v-tab ripple>Evaluierung</v-tab>
        <v-tab ripple>Aufgaben</v-tab>
        <v-tab ripple>Dokumente</v-tab>
        <v-tab ripple>Notizen</v-tab>
        <v-tab ripple>Historie</v-tab>

        <v-tab-item>
          <MasterDataForm :tom="tom" @save="save" />
        </v-tab-item>

        <v-tab-item>
          <ConfidentialityForm :tom="tom" @save="save" />
        </v-tab-item>

        <v-tab-item>
          <IntegrityForm :tom="tom" @save="save" />
        </v-tab-item>

        <v-tab-item>
          <EvaluationForm :tom="tom" @save="save" />
        </v-tab-item>

        <v-tab-item>
          <TaskList
            :parent-resource="'tom'"
            :parent-resource-id="tom.id"
            @save="save"
            path="/tasks"
          />
        </v-tab-item>

        <v-tab-item>
          <Documents
            :resource="'tom'"
            :id="tom.id"
            :allowed-file-types="['csv', 'doc', 'pdf', 'rtf', 'tex', 'txt', 'xlsx']"
          />
        </v-tab-item>

        <v-tab-item>
          <Notes :parent-resource="'tom'" :parent-resource-id="tom.id" @save="save" path="/notes" />
        </v-tab-item>
        <v-tab-item>
          <History :domain="'tom'" :domain-id="tom.id" path="/history" />
        </v-tab-item>
      </v-tabs>

      <rbx-confirm-dialog
        @confirm="onNewVersionConfirm" @refuse="onNewVersionRefuse"
        :message="'Sollen die Änderungen als neue Version dieses TOMs gespeichert werden?'"
        :is-displayed="isNewVersionConfirmDialogDisplayed"
        :is-loading="isLoading"
      />
    </template>
  </div>
</template>

<style lang="scss">
.tom-detail {
  &-title {
    margin-bottom: 10px;
  }

  &-actions {
    margin: 5px 0;

    .v-btn:first-child {
      border-radius: 5px 0 0 5px;
    }

    .v-btn:last-child {
      border-radius: 0 5px 5px 0;
    }

    .v-btn + .v-btn {
      border-left: 1px solid #fff !important;
    }

    .v-btn {
      margin: 0;
      width: 35px;
      height: 35px;
      border-radius: 0px;
    }
  }

  &-tab {
    padding-top: 10px;

    &-title {
      margin-bottom: 10px;
    }
  }

  &-common-list {
    margin-top: 5px;
    .v-btn {
      margin: 0px;
      width: 30px;
      height: 30px;
    }

    &-title {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 10px;
    }

    &-item {
      display: flex;
      align-items: center;
      justify-content: space-between;

      &:hover {
        .v-btn {
          opacity: 1;
        }
      }

      .v-btn {
        opacity: 0;
      }
    }
  }

  .v-tabs {
    &__items {
      padding-bottom: 30px;
    }
  }

  .submit-button {
    margin-top: 20px;
  }
}
</style>
